'use client';
import { VillageFutureBooking, VillagePriceVariants } from '@/services/types';
import useVillage from './useVillage';
import { addRomeDays } from '@/lib/rome-date';

type SelectedDates = {
  start: Date | null;
  end: Date | null;
};

export type CalendarDateInfos = {
  canCheckIn: boolean;
  enabledCheckInDays: string[];
  isOptioned: boolean;
  isBusy: boolean;
  isBeforeInvalidPeriod: boolean;
  isAfterInvalidPeriod: boolean;
  isBeforeOptionedDate: boolean;
  isAfterOptionedDate: boolean;
};

export default function useVillageDates() {
  const {
    getPriceVariant,
    getPricePeriod,
    getUnavailableDates,
    checkIfCanCheckIn,
  } = useVillage({});

  function getDateInfos({
    date,
    futureBookings,
    priceVariants,
  }: {
    date: Date;
    futureBookings: VillageFutureBooking[];
    priceVariants: VillagePriceVariants[];
  }) {
    const dateTime = date.getTime();

    const { busy, optioned } = getUnavailableDates(futureBookings);

    const { canCheckIn, checkInDays } = checkIfCanCheckIn(date, priceVariants);

    const isOptioned = optioned.some(({ timestampStart, timestampEnd }) => {
      const startTime = timestampStart;
      const endTime = timestampEnd;

      return dateTime >= startTime && dateTime <= endTime;
    });

    const isBusy = busy.some(({ timestampStart, timestampEnd }) => {
      const startTime = timestampStart;
      const endTime = timestampEnd;

      return dateTime >= startTime && dateTime <= endTime;
    });

    const isBeforeOptionedDate = optioned.some(({ timestampStart }) => {
      // Range ends are Rome instants: move them on the Rome clock
      const dayBeforeFirstOptionedDay = addRomeDays(timestampStart, -1);

      return dateTime >= dayBeforeFirstOptionedDay && dateTime < timestampStart;
    });

    const isBeforeInvalidPeriod = busy.some(({ timestampStart }) => {
      const dayBeforeFirstUnavailableDay = addRomeDays(timestampStart, -1);

      return (
        dateTime >= dayBeforeFirstUnavailableDay && dateTime < timestampStart
      );
    });

    const isAfterOptionedDate = optioned.some(({ timestampEnd }) => {
      const dayAfterLastOptionedDay = addRomeDays(timestampEnd, 1);

      return dateTime <= dayAfterLastOptionedDay && dateTime > timestampEnd;
    });

    const isAfterInvalidPeriod = busy.some(({ timestampEnd }) => {
      const dayAfterLastUnavailableDay = addRomeDays(timestampEnd, 1);

      return dateTime <= dayAfterLastUnavailableDay && dateTime > timestampEnd;
    });

    return {
      canCheckIn,
      enabledCheckInDays: checkInDays,
      isOptioned,
      isBusy,
      isBeforeInvalidPeriod,
      isAfterInvalidPeriod,
      isBeforeOptionedDate,
      isAfterOptionedDate,
    };
  }

  function getPriceVariantAndPeriod({
    priceVariants,
    selectedDates,
    totalBedrooms,
    totalGuests,
  }: {
    priceVariants: VillagePriceVariants[];
    selectedDates: SelectedDates;
    totalBedrooms: number;
    totalGuests: number;
  }) {
    const priceVariant = getPriceVariant({
      priceVariants,
      totalBedrooms,
      totalGuests,
    });

    if (priceVariant) {
      const pricePeriod = getPricePeriod({
        priceVariant,
        selectedDates,
      });

      if (!pricePeriod) {
        const nextYearPriceVariant = getPriceVariant({
          priceVariants,
          totalBedrooms,
          totalGuests,
          nextYear: true,
        });

        if (nextYearPriceVariant) {
          const pricePeriod = getPricePeriod({
            priceVariant: nextYearPriceVariant,
            selectedDates,
          });
          return {
            priceVariant: nextYearPriceVariant,
            pricePeriod,
          };
        }
      }

      if (pricePeriod) {
        return {
          priceVariant,
          pricePeriod,
        };
      }
    }
  }

  return {
    getPriceVariantAndPeriod,
    getDateInfos,
  };
}
